/**
 * MagicLinkConsumePage — user clicks the "Sign in" link in their email
 * → they land here → we consume the token and open a session.
 *
 * URL: /auth/magic-link/consume?token=XYZ
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { socialApi, formatApiError } from "../../lib/authApi";
import { setMemberSession } from "../../hooks/useMemberAuth";
import { AuthShell, Notice, PrimaryBtn } from "../../components/auth/AuthShell";

const MagicLinkConsumePage = () => {
  const nav = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [state, setState] = useState({ status: "processing", error: null });

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = new URLSearchParams(location.search).get("token");
    if (!token) {
      setState({ status: "error",
                 error: "This link is invalid or has already been used." });
      return;
    }

    (async () => {
      try {
        const { data } = await socialApi.consumeMagicLink(token);
        if (data.session_token) {
          await setMemberSession(data.session_token);
        }
        if (data.needs_profile_completion) {
          nav(`/auth/finish-profile?next=${encodeURIComponent("/auth/access-granted?welcome=1")}`);
        } else {
          // Route through Access Granted — repeat magic-link consumers
          // get the quiet variant; first-time (mode === 'signup') gets
          // the cinematic.
          const isFirst = data.mode === "signup";
          nav(`/auth/access-granted${isFirst ? "?welcome=1" : ""}`);
        }
      } catch (e) {
        setState({
          status: "error",
          error: formatApiError(e?.response?.data?.detail) || "Magic link is invalid or expired.",
        });
      }
    })();
  }, [nav, location.search]);

  return (
    <AuthShell
      title="Signing you in"
      lead="Consuming your one-time sign-in link…"
    >
      {state.status === "processing" && (
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#A39E93]"
             data-testid="magic-consume-processing">
          // WORKING…
        </div>
      )}
      {state.status === "error" && (
        <>
          <Notice kind="error" data-testid="magic-consume-error">{state.error}</Notice>
          <PrimaryBtn onClick={() => nav("/auth/login")} data-testid="magic-consume-back">
            RETURN TO SIGN-IN
          </PrimaryBtn>
        </>
      )}
    </AuthShell>
  );
};

export default MagicLinkConsumePage;
